// ═══════════════════════════════════════════════════════════════
// CanvasSettingsModal.js — Paramétrage canvas Family (P2)
// ═══════════════════════════════════════════════════════════════
// Panneau modal : choix des catégories actives, double-nationalité,
// alertes (cni_renewal v1). Upsert via PUT /api/projects/:id/family/canvas.
//
// Props :
//   projectId  : number
//   open       : boolean
//   onClose    : () => void
//   onSaved    : (canvas) => void    — callback après upsert réussi
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState, useMemo } from 'react';
import { X, Save, Loader2, Globe, Bell } from 'lucide-react';
import { getCanvas, upsertCanvas, listDocCategories } from '../../services/familyApi';
import DocCategoryChip from './DocCategoryChip';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from '../ui/Toast';

const ALERT_DEFS = [
    { key: 'cni_renewal', label: 'Renouvellement CNI / passeport', hint: 'Rappel 90 jours avant expiration' },
    { key: 'tax_deadline', label: 'Échéances fiscales', hint: 'Déclaration annuelle + acomptes' },
    { key: 'consular_act', label: 'Actes consulaires', hint: 'Actes état civil étrangers, double-nat' },
];

const CanvasSettingsModal = ({ projectId, open, onClose, onSaved }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [categories, setCategories] = useState([]);
    const [enabledCatIds, setEnabledCatIds] = useState(new Set());
    const [alerts, setAlerts] = useState({});
    const [dualNatEnabled, setDualNatEnabled] = useState(false);
    const [dualNatCodes, setDualNatCodes] = useState('');  // CSV edit-friendly

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        Promise.all([getCanvas(projectId), listDocCategories(projectId)])
            .then(([canvas, cats]) => {
                if (cancelled) return;
                setCategories(cats || []);
                setEnabledCatIds(new Set(canvas?.enabled_categories || []));
                setAlerts(canvas?.enabled_alerts || {});
                setDualNatEnabled(!!canvas?.dual_nationality_enabled);
                setDualNatCodes((canvas?.dual_nationality_codes || []).join(', '));
            })
            .catch((e) => toast.error(`Chargement canvas : ${e.message}`))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, projectId]);

    const toggleCat = (id) => {
        setEnabledCatIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleAlert = (key) => setAlerts((a) => ({ ...a, [key]: !a[key] }));

    const handleSave = async () => {
        setSaving(true);
        try {
            const codes = dualNatEnabled
                ? dualNatCodes.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 10)
                : null;
            const result = await upsertCanvas(projectId, {
                enabled_categories: Array.from(enabledCatIds),
                enabled_alerts: alerts,
                dual_nationality_enabled: dualNatEnabled,
                dual_nationality_codes: codes,
            });
            toast.success('Paramètres Family enregistrés');
            onSaved?.(result);
            onClose?.();
        } catch (e) {
            toast.error(`Enregistrement échoué : ${e.message}`);
        } finally {
            setSaving(false);
        }
    };

    const { systemCats, customCats } = useMemo(() => {
        const sys = categories.filter((c) => c.project_id === null);
        const cus = categories.filter((c) => c.project_id !== null);
        return { systemCats: sys, customCats: cus };
    }, [categories]);

    if (!open) return null;

    const surface = isDark ? '#1a1a2e' : '#ffffff';
    const surfaceAlt = isDark ? '#16162a' : '#f8fafc';
    const textPrimary = isDark ? '#e5e7eb' : '#1e293b';
    const textSecondary = isDark ? '#94a3b8' : '#64748b';
    const border = isDark ? '#2a2a44' : '#e2e8f0';

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 1100,
                background: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(15,23,42,0.5)',
                backdropFilter: 'blur(6px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24,
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: surface,
                    color: textPrimary,
                    borderRadius: 14,
                    width: '100%', maxWidth: 640,
                    maxHeight: '90vh', overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                    boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
                    border: `1px solid ${border}`,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{
                    padding: '18px 22px',
                    borderBottom: `1px solid ${border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 600 }}>Paramètres Famille</div>
                        <div style={{ fontSize: 12, color: textSecondary, marginTop: 2 }}>
                            Canvas du projet — catégories, double-nationalité, alertes
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent', border: 'none', color: textSecondary,
                            cursor: 'pointer', padding: 4, borderRadius: 6,
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: 22, overflowY: 'auto', flex: 1 }}>
                    {loading ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
                            <Loader2 size={22} className="animate-spin" />
                        </div>
                    ) : (
                        <>
                            {/* Section Catégories */}
                            <Section title="Catégories actives" textSecondary={textSecondary}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {systemCats.map((cat) => (
                                        <CategoryToggle
                                            key={cat.id}
                                            category={cat}
                                            active={enabledCatIds.has(cat.id)}
                                            onToggle={() => toggleCat(cat.id)}
                                        />
                                    ))}
                                </div>
                                {customCats.length > 0 && (
                                    <div style={{ marginTop: 12 }}>
                                        <div style={{ fontSize: 11, color: textSecondary, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Catégories personnalisées
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                            {customCats.map((cat) => (
                                                <CategoryToggle
                                                    key={cat.id}
                                                    category={cat}
                                                    active={enabledCatIds.has(cat.id)}
                                                    onToggle={() => toggleCat(cat.id)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </Section>

                            {/* Section Double-nationalité */}
                            <Section
                                title={<><Globe size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Double-nationalité</>}
                                textSecondary={textSecondary}
                            >
                                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 0' }}>
                                    <input
                                        type="checkbox"
                                        checked={dualNatEnabled}
                                        onChange={(e) => setDualNatEnabled(e.target.checked)}
                                        style={{ width: 16, height: 16 }}
                                    />
                                    <span style={{ fontSize: 13 }}>
                                        Activer le workflow double-nationalité (onglet consulaire, tag sur les documents)
                                    </span>
                                </label>
                                {dualNatEnabled && (
                                    <div style={{ marginTop: 8 }}>
                                        <div style={{ fontSize: 11, color: textSecondary, marginBottom: 6 }}>
                                            Codes pays (ISO-3166, séparés par virgule) — ex : <code>FR, SN</code>
                                        </div>
                                        <input
                                            type="text"
                                            value={dualNatCodes}
                                            onChange={(e) => setDualNatCodes(e.target.value)}
                                            placeholder="FR, SN"
                                            style={{
                                                width: '100%', padding: '8px 10px',
                                                background: surfaceAlt, color: textPrimary,
                                                border: `1px solid ${border}`, borderRadius: 8,
                                                fontSize: 13, outline: 'none',
                                                fontFamily: 'ui-monospace, monospace',
                                            }}
                                        />
                                    </div>
                                )}
                            </Section>

                            {/* Section Alertes */}
                            <Section
                                title={<><Bell size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Alertes</>}
                                textSecondary={textSecondary}
                            >
                                {ALERT_DEFS.map((a) => (
                                    <label
                                        key={a.key}
                                        style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '6px 0' }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={!!alerts[a.key]}
                                            onChange={() => toggleAlert(a.key)}
                                            style={{ width: 16, height: 16, marginTop: 2 }}
                                        />
                                        <div>
                                            <div style={{ fontSize: 13 }}>{a.label}</div>
                                            <div style={{ fontSize: 11, color: textSecondary, marginTop: 1 }}>{a.hint}</div>
                                        </div>
                                    </label>
                                ))}
                            </Section>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '14px 22px',
                    borderTop: `1px solid ${border}`,
                    display: 'flex', justifyContent: 'flex-end', gap: 8,
                    background: surfaceAlt,
                }}>
                    <button
                        onClick={onClose}
                        disabled={saving}
                        style={{
                            background: 'transparent', color: textSecondary,
                            border: `1px solid ${border}`, borderRadius: 8,
                            padding: '8px 14px', fontSize: 13, cursor: 'pointer',
                        }}
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving || loading}
                        style={{
                            background: '#1D4ED8', color: '#fff',
                            border: 'none', borderRadius: 8,
                            padding: '8px 16px', fontSize: 13, fontWeight: 500,
                            cursor: saving ? 'wait' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: 6,
                            opacity: saving || loading ? 0.65 : 1,
                        }}
                    >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        Enregistrer
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Subcomponents ──────────────────────────────────────────────
const Section = ({ title, children, textSecondary }) => (
    <div style={{ marginBottom: 20 }}>
        <div style={{
            fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
            color: textSecondary, marginBottom: 10,
        }}>
            {title}
        </div>
        {children}
    </div>
);

const CategoryToggle = ({ category, active, onToggle }) => {
    const opacity = active ? 1 : 0.45;
    return (
        <span
            onClick={onToggle}
            style={{ cursor: 'pointer', opacity, transition: 'opacity 0.15s' }}
            title={active ? 'Activée — cliquer pour désactiver' : 'Désactivée — cliquer pour activer'}
        >
            <DocCategoryChip category={category} size="md" />
        </span>
    );
};

export default CanvasSettingsModal;
